/**
 * SaveRoutingManager - Routes saves based on connectivity status
 * 
 * Online: Direct database saves (bypass working copy)
 * Offline: Working copy staging
 */

import { ConnectivityManager, ConnectivityState } from './ConnectivityManager';
import { DMSDocumentService } from './DMSDocumentService';
import { LocalDocumentState } from '../types/DocumentTypes';

export interface SaveResult {
  success: boolean;
  message: string;
  saveType: 'direct' | 'working-copy' | 'failed';
  timestamp: Date;
  errors?: string[];
}

export interface PendingWorkingCopy {
  documentId: string;
  content: string;
  timestamp: Date;
  metadata?: any;
}

export class SaveRoutingManager {
  private connectivityManager: ConnectivityManager;
  private dmsService: DMSDocumentService;
  private pendingWorkingCopies: Map<string, PendingWorkingCopy> = new Map();

  constructor(connectivityManager: ConnectivityManager, dmsService: DMSDocumentService) {
    this.connectivityManager = connectivityManager;
    this.dmsService = dmsService;

    // Listen for connectivity changes
    this.connectivityManager.addListener({
      onOnline: this.handleBackOnline.bind(this),
      onOffline: this.handleGoOffline.bind(this),
      onConnectivityChange: this.handleConnectivityChange.bind(this)
    });

    console.log('[SaveRoutingManager] Initialized');
  }

  /**
   * Route save based on connectivity status AND checkout status
   */
  async saveDocument(documentId: string, content: string, metadata?: any, isCheckedOut?: boolean): Promise<SaveResult> {
    const isOnline = this.connectivityManager.isOnline();
    
    console.log(`[SaveRoutingManager] Routing save for document ${documentId}, online: ${isOnline}, checkedOut: ${isCheckedOut}`);

    if (isOnline) {
      // ONLINE: Always save directly to database (no checkout required)
      return await this.saveDirectToDatabase(documentId, content, metadata);
    } else {
      // OFFLINE: Only save to working copy if document is checked out
      if (isCheckedOut) {
        return await this.saveToWorkingCopy(documentId, content, metadata);
      } else {
        // OFFLINE + NOT CHECKED OUT: Cannot save
        return {
          success: false,
          message: 'Cannot save offline: document must be checked out for offline editing',
          saveType: 'failed',
          timestamp: new Date()
        };
      }
    }
  }

  /**
   * ONLINE: Save directly to database (current version)
   */
  private async saveDirectToDatabase(documentId: string, content: string, metadata?: any): Promise<SaveResult> {
    try {
      console.log('[SaveRoutingManager] 🌐 ONLINE: Saving directly to database');
      
      // Use the existing DMS service to save directly to current version
      // This bypasses working copy entirely
      const result = await this.dmsService.updateDocumentContent(documentId, content, metadata);
      
      if (result.success) {
        console.log('[SaveRoutingManager] ✅ Direct database save successful');
        return {
          success: true,
          message: 'Document saved directly to database',
          saveType: 'direct',
          timestamp: new Date()
        };
      } else {
        // NO FALLBACK: Online save should not use working copy
        console.error('[SaveRoutingManager] ❌ Direct save failed, no fallback');
        return {
          success: false,
          message: 'Failed to save directly to database',
          saveType: 'failed',
          timestamp: new Date()
        };
      }
    } catch (error) {
      console.error('[SaveRoutingManager] Direct database save error:', error);
      
      // NO FALLBACK: Online save should not use working copy
      return {
        success: false,
        message: `Database save error: ${error instanceof Error ? error.message : String(error)}`,
        saveType: 'failed',
        timestamp: new Date()
      };
    }
  }

  /**
   * OFFLINE: Save to working copy (temporary staging)
   */
  private async saveToWorkingCopy(documentId: string, content: string, metadata?: any): Promise<SaveResult> {
    try {
      console.log('[SaveRoutingManager] 📴 OFFLINE: Saving to working copy');
      
      // Store in pending working copies for later commit
      this.pendingWorkingCopies.set(documentId, {
        documentId,
        content,
        timestamp: new Date(),
        metadata
      });

      // Try to save to server working copy if possible
      try {
        const result = await this.dmsService.saveWorkingCopy(documentId, content);
        if (result.success) {
          console.log('[SaveRoutingManager] ✅ Working copy saved to server');
          return {
            success: true,
            message: 'Document saved to working copy (offline mode)',
            saveType: 'working-copy',
            timestamp: new Date()
          };
        }
      } catch (error) {
        console.warn('[SaveRoutingManager] Server working copy save failed, keeping local copy');
      }

      // Keep local copy even if server save fails
      return {
        success: true,
        message: 'Document saved locally (offline mode)',
        saveType: 'working-copy',
        timestamp: new Date()
      };

    } catch (error) {
      console.error('[SaveRoutingManager] Working copy save error:', error);
      return {
        success: false,
        message: `Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        saveType: 'failed',
        timestamp: new Date(),
        errors: [error instanceof Error ? error.message : 'Unknown error']
      };
    }
  }

  /**
   * Handle coming back online - commit pending working copies
   */
  private async handleBackOnline(): Promise<void> {
    console.log('[SaveRoutingManager] 🌐 Back online - committing pending working copies');
    
    if (this.pendingWorkingCopies.size === 0) {
      console.log('[SaveRoutingManager] No pending working copies to commit');
      return;
    }

    const commitPromises: Promise<void>[] = [];
    
    for (const [documentId, workingCopy] of this.pendingWorkingCopies) {
      commitPromises.push(this.commitWorkingCopyToDatabase(documentId, workingCopy));
    }

    try {
      await Promise.all(commitPromises);
      console.log('[SaveRoutingManager] ✅ All working copies committed successfully');
    } catch (error) {
      console.error('[SaveRoutingManager] Some working copy commits failed:', error);
    }
  }

  /**
   * Commit a working copy to the database and clear it
   */
  private async commitWorkingCopyToDatabase(documentId: string, workingCopy: PendingWorkingCopy): Promise<void> {
    try {
      console.log(`[SaveRoutingManager] Committing working copy for document ${documentId}`);
      
      // Save directly to current version (not working copy)
      const result = await this.dmsService.updateDocumentContent(
        documentId, 
        workingCopy.content, 
        workingCopy.metadata
      );

      if (result.success) {
        // Clear the working copy after successful commit
        this.pendingWorkingCopies.delete(documentId);
        
        // Also clear server-side working copy
        try {
          await this.dmsService.clearWorkingCopy(documentId);
        } catch (error) {
          console.warn('[SaveRoutingManager] Failed to clear server working copy:', error);
        }
        
        console.log(`[SaveRoutingManager] ✅ Working copy committed and cleared for document ${documentId}`);
      } else {
        console.error(`[SaveRoutingManager] Failed to commit working copy for document ${documentId}:`, result.message);
      }
    } catch (error) {
      console.error(`[SaveRoutingManager] Error committing working copy for document ${documentId}:`, error);
    }
  }

  /**
   * Handle going offline
   */
  private handleGoOffline(): void {
    console.log('[SaveRoutingManager] 📴 Gone offline - switching to working copy mode');
  }

  /**
   * Handle connectivity state changes
   */
  private handleConnectivityChange(state: ConnectivityState): void {
    console.log('[SaveRoutingManager] Connectivity changed:', state);
  }

  /**
   * Get pending working copies count
   */
  getPendingWorkingCopiesCount(): number {
    return this.pendingWorkingCopies.size;
  }

  /**
   * Get pending working copies for a document
   */
  getPendingWorkingCopy(documentId: string): PendingWorkingCopy | undefined {
    return this.pendingWorkingCopies.get(documentId);
  }

  /**
   * Manually commit all pending working copies
   */
  async commitAllPendingWorkingCopies(): Promise<void> {
    await this.handleBackOnline();
  }

  /**
   * Clear all pending working copies (use with caution)
   */
  clearAllPendingWorkingCopies(): void {
    this.pendingWorkingCopies.clear();
    console.log('[SaveRoutingManager] All pending working copies cleared');
  }

  /**
   * Destroy the save routing manager
   */
  destroy(): void {
    this.pendingWorkingCopies.clear();
    console.log('[SaveRoutingManager] Destroyed');
  }
}
